/**
 * Chat message bubble component
 */

import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { ChatMessage as ChatMessageType } from '../../types/chat';
import ConfirmationCard from './ConfirmationCard';

interface ChatMessageProps {
  message: ChatMessageType;
  onConfirm?: () => void;
  onCancel?: () => void;
  isConfirming?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onConfirm,
  onCancel,
  isConfirming = false
}) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          maxWidth: '85%',
          p: 1.5,
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'white' : 'text.primary',
          borderRadius: 2,
          borderTopRightRadius: isUser ? 0 : 2,
          borderTopLeftRadius: isUser ? 2 : 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>

        {message.confirmation && (
          <Box sx={{ mt: 2 }}>
            <ConfirmationCard
              confirmation={message.confirmation}
              onConfirm={onConfirm}
              onCancel={onCancel}
              isConfirming={isConfirming}
            />
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            opacity: 0.7,
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      </Paper>
    </Box>
  );
};

export default ChatMessage;
